import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, Ship, Train, Truck, Plane, Route } from 'lucide-react';

function FooterLink({ to, children }) {
  return (
    <Link to={to} className="text-[11px] text-gray-500 hover:text-[#213970] font-bold transition">
      {children}
    </Link>
  );
}

function SocialButton({ label, children }) {
  return (
    <button
      type="button"
      disabled
      aria-disabled="true"
      title="به‌زودی"
      className="w-11 h-11 rounded-2xl border border-gray-200 bg-gray-50 text-gray-400 flex items-center justify-center cursor-not-allowed opacity-70"
    >
      <span className="sr-only">{label}</span>
      {children}
    </button>
  );
}

function BaleIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.18" />
      <path
        d="M9.2 7.9h3.9c2.3 0 3.7 1.2 3.7 3.2 0 2-1.4 3.3-3.7 3.3H12v2.7H9.2V7.9Zm2.8 2.3v2.9h1.1c.9 0 1.4-.5 1.4-1.4 0-.9-.5-1.5-1.4-1.5H12Z"
        fill="currentColor"
      />
    </svg>
  );
}

function EitaaIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.18" />
      <path
        d="M8.3 7.9h7.4v2.2h-4.6v1.8h4.1v2.1h-4.1v1.9h4.7v2.2H8.3V7.9Z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function SiteFooter() {
  return (
    <footer className="bg-blue-50 border-t border-blue-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-1">
            <div className="text-sm font-black text-gray-900">کریدور هوشمند شمال - جنوب</div>
            <div className="text-[11px] text-gray-500 leading-7 mt-3">
              درگاه یکپارچه برای تجارت فرامرزی، حمل‌ونقل چندوجهی و فرصت‌های سرمایه‌گذاری در مسیر راهبردی جنوب به شمال.
            </div>

            <div className="mt-5 flex items-center gap-2">
              <SocialButton label="بله">
                <span className="text-[#213970]">
                  <BaleIcon />
                </span>
              </SocialButton>
              <SocialButton label="ایتا">
                <span className="text-[#213970]">
                  <EitaaIcon />
                </span>
              </SocialButton>
            </div>
            <div className="text-[10px] text-gray-400 mt-2 font-bold">شبکه‌های اجتماعی (به‌زودی)</div>
          </div>

          <div>
            <div className="text-[12px] font-black text-gray-800">دسترسی سریع</div>
            <div className="mt-4 space-y-3">
              <FooterLink to="/dashboard">داشبورد</FooterLink>
              <div>
                <FooterLink to="/trade">تجارت</FooterLink>
              </div>
              <div>
                <FooterLink to="/transport">حمل‌ونقل</FooterLink>
              </div>
              <div>
                <FooterLink to="/investment">سرمایه‌گذاری</FooterLink>
              </div>
              <div>
                <FooterLink to="/systems/tracking">رهگیری چندوجهی</FooterLink>
              </div>
              <div>
                <FooterLink to="/about">درباره ما</FooterLink>
              </div>
            </div>
          </div>

          <div>
            <div className="text-[12px] font-black text-gray-800">خدمات مستقیم</div>
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-2">
                <Ship size={16} className="text-blue-700" />
                <FooterLink to="/services/maritime">دریایی</FooterLink>
              </div>
              <div className="flex items-center gap-2">
                <Train size={16} className="text-blue-700" />
                <FooterLink to="/services/rail">ریلی</FooterLink>
              </div>
              <div className="flex items-center gap-2">
                <Truck size={16} className="text-blue-700" />
                <FooterLink to="/services/road">جاده‌ای</FooterLink>
              </div>
              <div className="flex items-center gap-2">
                <Plane size={16} className="text-blue-700" />
                <FooterLink to="/services/air-cargo">هوایی (کارگو)</FooterLink>
              </div>
              <div className="flex items-center gap-2">
                <Route size={16} className="text-blue-700" />
                <FooterLink to="/systems/permits-partnership">مجوزها / مدل‌های مشارکت</FooterLink>
              </div>
            </div>
          </div>

          <div>
            <div className="text-[12px] font-black text-gray-800">پشتیبانی و تماس</div>
            <div className="mt-4 space-y-3 text-[11px] text-gray-600 font-bold">
              <div className="flex items-center gap-2">
                <Phone size={16} className="text-gray-400" />
                <span>۰۲۱-۰۰۰۰۰۰۰۰</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail size={16} className="text-gray-400" />
                <span>support@corridor.ir</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-gray-400" />
                <span>تهران (نمونه) — مرکز عملیات کریدور</span>
              </div>
              <div className="pt-2 text-[10px] text-gray-400 font-extrabold">
                ساعات پاسخگویی: شنبه تا چهارشنبه، ۹ تا ۱۷
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-blue-100 flex items-center justify-center">
          <div className="text-[10px] text-gray-400 font-bold text-center">
            تمامی حقوق برای <span className="text-gray-500">پیمان متین اطلس</span> محفوظ است.
          </div>
        </div>
      </div>
    </footer>
  );
}

